/* eslint-env jest */
import { Action, combineReducers, createStore, Dispatch, Reducer } from 'redux';

import type {
  ActionTypes,
  DuckOptions,
  EffectTypes,
  GlobalReduxState,
  SelectorTypes,
} from '@rover/react-lib/src/lib/redux-duck/types';
import { ApiErrorMessage } from '@rover/utilities/apiErrorMessage';

const ERROR_MESSAGE_FIXTURE = ApiErrorMessage.fromApiResponse('Error Message');

type RequiredState = {
  loading: boolean;
  errorMessage: ApiErrorMessage | null;
};

const generateActions = <DuckState extends RequiredState>(action: string): any => ({
  [`${action}Pending`]: () => (state: DuckState) => ({
    ...state,
    loading: true,
    errorMessage: null,
  }),
  [`${action}Success`]: (response) => (state: DuckState) => ({
    ...state,
    [`${action}Response`]: response,
    loading: false,
    errorMessage: null,
  }),
  [`${action}Failure`]: (errorMessage: ApiErrorMessage) => (state: DuckState) => ({
    ...state,
    errorMessage,
    loading: false,
  }),
});

export const generateActionTests = <
  DuckState extends RequiredState,
  DuckActions extends ActionTypes,
  DuckEffects extends EffectTypes,
  DuckSelectors extends SelectorTypes
>(
  action: string,
  responseFixture: Record<string, any>,
  actions: DuckActions,
  options: DuckOptions<DuckState, DuckActions, DuckEffects, DuckSelectors>,
  reducer: Reducer<DuckState>
): any => {
  describe(action, () => {
    let dispatch: Dispatch<Action<string>>;
    let getState: () => { test: DuckState } & GlobalReduxState;

    beforeEach(() => {
      ({ dispatch, getState } = createStore(
        combineReducers({
          test: reducer,
        })
      ));
    });

    it(`sets ${action}Pending state`, () => {
      expect(getState().test.loading).toBe(options.initialState.loading);
      dispatch(actions[`${action}Pending`](null));
      expect(getState().test.loading).toBe(true);
    });

    it(`sets ${action}Failure state`, () => {
      expect(getState().test.errorMessage).toEqual(options.initialState.errorMessage);
      dispatch(actions[`${action}Failure`](ERROR_MESSAGE_FIXTURE));
      expect(getState().test.errorMessage).toEqual(ERROR_MESSAGE_FIXTURE);
    });

    it(`sets ${action}Success state`, () => {
      expect(getState().test[`${action}Response`]).toEqual(
        options.initialState[`${action}Response`]
      );
      dispatch(actions[`${action}Success`](responseFixture));
      expect(getState().test[`${action}Response`]).toEqual(responseFixture);
    });
  });
};

export default generateActions;
